import { el, clear, showToast, getISOWeek, statusLabel, t } from './dom';
import { api, ApiError } from './api';
import type { Order, User } from './types';
import { renderOrderEditor } from './orderEditorView';
import { buildLanguageSelector } from './languageSelector';
import { openChangePasswordModal } from './changePasswordModal';
import { COMPANY } from './config/company';

export async function renderEmployeeDashboard(root: HTMLElement, user: User, onLogout: () => void) {
  clear(root);

  // Header con logo
  const header = el('header', { className: 'header' });

  const brand = el('div', { className: 'header-brand' });
  const logoImg = document.createElement('img');
  logoImg.src = COMPANY.logoUrl;
  logoImg.alt = COMPANY.name;
  logoImg.className = 'header-logo';
  brand.appendChild(logoImg);
  brand.appendChild(el('h1', { className: 'header-title', text: COMPANY.name }));

  header.append(
    brand,
    el('div', { className: 'user-info' }, [
      buildLanguageSelector(() => renderEmployeeDashboard(root, user, onLogout)),
      el('button', {
        className: 'btn-logout',
        text: `🔐 ${t('account.changePassword')}`,
        onClick: () => openChangePasswordModal(),
      }),
      el('span', { text: user.email }),
      el('button', {
        className: 'btn-logout',
        text: t('header.logout'),
        onClick: onLogout,
      }),
    ]),
  );

  const main = el('main', { className: 'main' });

  // Toolbar
  const toolbar = el('div', { className: 'toolbar' });
  const newBtn = el('button', {
    className: 'btn btn-primary',
    text: t('orders.newOrder'),
    onClick: () => createNewOrder(),
  });
  toolbar.appendChild(newBtn);

  const listContainer = el('div', { className: 'order-list-container' });

  const refresh = async () => {
    clear(listContainer);
    listContainer.appendChild(el('p', { text: t('common.loading'), className: 'loading' }));

    try {
      const data = await api.request<{ orders: Order[]; total: number }>(
        'GET',
        '/orders/mine?page=1&pageSize=20',
      );

      clear(listContainer);

      if (data.orders.length === 0) {
        listContainer.appendChild(el('div', {
          className: 'empty-state',
          text: t('orders.empty'),
        }));
        return;
      }

      const ul = el('ul', { className: 'order-list' });
      for (const order of data.orders) {
        const li = el('li', {
          className: 'order-item',
          onClick: () => openOrder(order.id),
        });

        const titleRow = el('div', { className: 'card-header' });
        titleRow.append(
          el('span', { className: 'card-title', text: order.orderNumber }),
          el('span', { className: `badge badge-${order.status}`, text: statusLabel(order.status) }),
        );

        const meta = el('div', { className: 'order-meta' });
        meta.textContent = `${t('orders.weekOf', { week: order.weekNumber, year: order.year })} · ${t('orders.itemCount', { n: order.items.length })}`;

        li.append(titleRow, meta);
        ul.appendChild(li);
      }
      listContainer.appendChild(ul);
    } catch (e) {
      clear(listContainer);
      const msg = e instanceof ApiError ? e.message : t('common.networkError');
      listContainer.appendChild(el('div', { className: 'empty-state', text: msg }));
    }
  };

  const createNewOrder = async () => {
    const { week, year } = getISOWeek(new Date());
    try {
      const order = await api.request<Order>('POST', '/orders', {
        weekNumber: week,
        year,
      });
      showToast(t('orders.orderCreated'), 'success');
      openOrder(order.id);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : t('common.unknownError');
      showToast(msg, 'error');
    }
  };

  const openOrder = (orderId: string) => {
    renderOrderEditor(root, orderId, user, () => renderEmployeeDashboard(root, user, onLogout));
  };

  main.append(toolbar, listContainer);
  root.append(header, main);

  await refresh();
}
